import React, { useRef, useEffect } from "react";

interface HeaderPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onClearAllData: () => void;
  onClearAllExpenses: () => void;
  anchorElement: HTMLElement | null;
  hasExpenses: boolean;
}

const HeaderPopup: React.FC<HeaderPopupProps> = ({
  isOpen,
  onClose,
  onClearAllData,
  onClearAllExpenses,
  anchorElement,
  hasExpenses,
}) => {
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        anchorElement &&
        !anchorElement.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, onClose, anchorElement]);

  const getPopupPosition = () => {
    if (!anchorElement) return { top: 0, left: 0 };

    const rect = anchorElement.getBoundingClientRect();
    const isMobile = window.innerWidth <= 768;
    const popupWidth = isMobile ? 200 : 250; // Responsive popup width
    const popupHeight = 120; // Fixed height since we always show both buttons

    let top = rect.bottom + 8;
    let left = rect.left;

    // For mobile, center the popup horizontally if it would go off screen
    if (isMobile && left + popupWidth > window.innerWidth) {
      left = Math.max(8, (window.innerWidth - popupWidth) / 2);
    } else if (left + popupWidth > window.innerWidth) {
      left = rect.right - popupWidth;
    }

    if (top + popupHeight > window.innerHeight) {
      top = rect.top - popupHeight - 8;
    }

    // Ensure popup doesn't go off the left edge
    if (left < 8) {
      left = 8;
    }

    // Ensure popup doesn't go off the right edge
    if (left + popupWidth > window.innerWidth - 8) {
      left = window.innerWidth - popupWidth - 8;
    }

    return { top, left };
  };

  const handleClearAllData = () => {
    onClearAllData();
    onClose();
  };

  const handleClearAllExpenses = () => {
    onClearAllExpenses();
    onClose();
  };

  if (!isOpen) return null;

  const position = getPopupPosition();

  return (
    <div
      ref={popupRef}
      className="header-popup"
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        zIndex: 1000,
      }}
    >
      <div className="header-popup-content">
        <button
          onClick={handleClearAllData}
          className="popup-action-btn clear-all-data"
        >
          <i className="fas fa-broom"></i>
          Alles löschen
        </button>

        <button
          onClick={handleClearAllExpenses}
          className={`popup-action-btn clear-all-expenses ${!hasExpenses ? "disabled" : ""}`}
          disabled={!hasExpenses}
        >
          <i className="fas fa-list"></i>
          Alle Ausgaben löschen
        </button>
      </div>
    </div>
  );
};

export default HeaderPopup;
